import { ExternalIntegrationEnum, ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { AssignedProperty, PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { SocialChannelType } from 'common/ShareSheetV2/Screens/Default/Default'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { MediaFileMetadata } from './ArticlePostPreviewContext'
import { reportError } from 'utils/error'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { DesignChannel } from 'components/NewFileModal/Types'

export const getDateAndStatusFromProperties = (properties: AssignedProperty[]) => {
  let status: PostStatusPropertyValue | undefined
  let postAt: string | undefined
  let channel: string | undefined
  properties.forEach((property) => {
    if (property.property.name === 'Post Status' || property.property.name === 'Post Date & Time') {
      const value = property.property_values[0]
      if (property.property.name === 'Post Status') {
        status = value.value as PostStatusPropertyValue
      } else if (property.property.name === 'Post Date & Time') {
        postAt = value?.value
      }
    } else if (property.property.name === 'Channel') {
      channel = property.property_values[0].value as string
    }
  })
  return { status, postAt, channel }
}

// Static provider mapping for non-blog channels
export const providerFromChannelType: Record<
  SocialChannelType | ChannelType.BlogPost | ChannelType.Newsletter,
  ExternalIntegrationEnum
> = {
  [ChannelType.FacebookPost]: ExternalIntegrationEnum.Facebook,
  [ChannelType.InstagramPosts]: ExternalIntegrationEnum.Instagram,
  [ChannelType.InstagramReel]: ExternalIntegrationEnum.Instagram,
  [ChannelType.LinkedInPosts]: ExternalIntegrationEnum.LinkedIn,
  [ChannelType.Tweets]: ExternalIntegrationEnum.X,
  [ChannelType.TweetThread]: ExternalIntegrationEnum.X,
  [ChannelType.BlogPost]: ExternalIntegrationEnum.WordPress,
  [ChannelType.Newsletter]: ExternalIntegrationEnum.Mailchimp,
  [DesignChannel.LinkedInPost]: ExternalIntegrationEnum.LinkedIn,
  [DesignChannel.InstagramPost]: ExternalIntegrationEnum.Instagram,
  [DesignChannel.InstagramStory]: ExternalIntegrationEnum.Instagram,
  [DesignChannel.TwitterPost]: ExternalIntegrationEnum.X,
  [DesignChannel.YoutubeShort]: ExternalIntegrationEnum.YouTube,
  [DesignChannel.TikTokPost]: ExternalIntegrationEnum.TikTok,
}

// Blog providers that are supported for blog posts
export const supportedBlogProviders: ExternalIntegrationEnum[] = [
  ExternalIntegrationEnum.WordPress,
  ExternalIntegrationEnum.GoHighLevel,
  ExternalIntegrationEnum.Wix,
]

/**
 * Check if a provider is a blog provider
 */
export const isBlogProvider = (provider: ExternalIntegrationEnum): boolean => {
  return supportedBlogProviders.includes(provider)
}

/**
 * Get the provider for a channel, with dynamic resolution for blog posts
 * @param channel - The channel type
 * @param integration - Optional integration to determine blog provider from
 * @returns The provider enum
 */
export const getProviderForChannel = (
  channel: SocialChannelType | ChannelType | DesignChannel,
  integration?: ExternalIntegration,
): ExternalIntegrationEnum | undefined => {
  if (channel === ChannelType.BlogPost && integration && isBlogProvider(integration.provider)) {
    return integration.provider
  }

  return providerFromChannelType[channel as keyof typeof providerFromChannelType]
}

/**
 * Get the provider from an integration ID by looking up the integration
 * @param integrationId - The integration ID
 * @param availableIntegrations - Array of available integrations
 * @returns The provider enum or undefined if not found
 */
export const getProviderFromIntegrationId = (
  integrationId: number,
  availableIntegrations: ExternalIntegration[],
): ExternalIntegrationEnum | undefined => {
  const integration = availableIntegrations.find((int) => int.id === integrationId)
  return integration?.provider
}

export const getVideoMetadata = async (videoUrl: string): Promise<MediaFileMetadata> => {
  const truncateNumber = (n: number, fixed: number) => ~~(Math.pow(10, fixed) * n) / Math.pow(10, fixed)

  return new Promise((resolve: (metadata: MediaFileMetadata) => void) => {
    let thumbnail_url: string
    let canvas: HTMLCanvasElement = document.createElement('canvas')
    let context: CanvasRenderingContext2D | null = canvas.getContext('2d')

    let video = document.createElement('video')
    video.preload = 'auto'
    video.addEventListener('loadeddata', async () => {
      if (!context) return

      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      thumbnail_url = await getVideoFrame(video, context, canvas)

      resolve({
        size: 0, // Will be set by the caller, which has access to the file object
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration,
        frame_rate: 0,
        aspect_ratio: truncateNumber(video.videoWidth / video.videoHeight, 2),
        thumbnail_url,
        progress: 0,
      })
    })
    video.src = videoUrl
    video.crossOrigin = 'anonymous'
    video.load()
  })
}

const getVideoFrame = async (
  video: HTMLVideoElement,
  context: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
): Promise<string> => {
  return new Promise((resolve: (frame: string) => void) => {
    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked)
      context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight)
      resolve(canvas.toDataURL())
    }
    video.addEventListener('seeked', onSeeked)
    video.currentTime = 1
  })
}

export const getImageMetadata = async (file: File) => {
  return new Promise((resolve: (thumbnail_url: string) => void) => {
    var reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      if (!reader.result) return

      resolve(reader.result as string)
    }
    reader.onerror = (error) => {
      reportError(error)
      showErrorMessageToast("There was an issue extracting the image's thumbnail.")
    }
  })
}
